import { CommonModule } from "@angular/common";
import { Component, inject, OnInit, output, signal } from "@angular/core";
import { MatExpansionModule } from "@angular/material/expansion";
import { AuthService } from "../../auth/auth.service";
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from "@angular/forms";
import { noWhitespaceValidator } from '../../utils/no-whitespace.validator';
import { IndicationQuoteStepService } from "../../services/indicationQuoteStep.service";
import { ConfirmationDialogComponent } from "../utils/confirmation-dialog/confirmation-dialog.component";
import { MatDialog, MatDialogModule } from "@angular/material/dialog";
import { ToastService } from "../../services/toast.service";
import { ToastType } from "../../enums/config.enum";
import { Router } from "@angular/router";
import { PolicyAdministrationService } from "../../services/policyAdministration.service";
import { MTAChangeTypeList } from "../../models/RequestQuote";
import { forkJoin, map } from "rxjs";
import moment from 'moment';
import { EndorsementDocument } from "../endorsement-document/endorsement-document.component";
import { PolicyDocumentService } from "../../services/policyDocument.service";


@Component({
    selector: 'app-policy-administration',
    standalone: true,
    imports: [CommonModule, MatExpansionModule, MatDialogModule, FormsModule, ReactiveFormsModule, EndorsementDocument],
    templateUrl: './policy-administration.component.html',
    styleUrl: './policy-administration.component.scss',
})
export class PolicyAdministration implements OnInit{
    mtaChangeList = signal<MTAChangeTypeList[]>([]);
    fb: FormBuilder = inject(FormBuilder);
    dialog = inject(MatDialog);
    authService = inject(AuthService);
    toastService = inject(ToastService);
    policyService = inject(PolicyAdministrationService);
    policyDocumentService = inject(PolicyDocumentService);
    router = inject(Router);
    indicationQuoteService = inject(IndicationQuoteStepService);
    isReadOnly = signal<boolean>(false);
    policyAdministrationForm: any = this.fb.group({
        requestChangeType: ['', [Validators.required, noWhitespaceValidator]],
        requestDescription: [''],
        responseDescription: [''],
        requestUserResponse: [''],
        responseChangeBound: [''],
        reviewUserResponse: [''],
        adjustmentLocked: [''],
        requestDate: ['', [Validators.required, noWhitespaceValidator]]
    });
    today = signal(moment().format('YYYY-MM-DD'))
    policyStartDate = signal<string>('');
    quoteMTAEmitter = output<any>();
    isQuoteMTA = signal<boolean>(false);

    ngOnInit(): void {
        this.isQuoteMTA.set(this.policyService.isQuoteMTA());
        const getPolicyStartDate = sessionStorage.getItem('policyDate');
        if(getPolicyStartDate){
            const policyStartDateFromStorage = moment(getPolicyStartDate).format('YYYY-MM-DD');
            this.policyStartDate.set(policyStartDateFromStorage);
        }
        this.fetchPolicyMTAList();
        //this.fetchPolicyAdminsitrationDetails();
    }

    formatRequestDate(requestDate: string){ 
        return moment(requestDate).format('YYYY-MM-DD');
    }

    fetchBindIssuePolicyDetailsByPolicy() {
        this.indicationQuoteService.fetchBindIssuePolicyDetailsByPolicyId(Number(this.authService.getUserDetailsFromStorage()?.policyId), 
        Number(this.authService.getUserDetailsFromStorage()?.userDetails?.userId))
        .subscribe({
            next:(response: any) => {
                if(response){
                    const {policyStartDate} = response?.result;
                    this.policyStartDate.set(this.formatRequestDate(policyStartDate));
                }
            },
            error: (error: any) => {
                console.log("error", error);
            }
        })
    }

    fetchPolicyMTAList() {
        this.policyService.getPolicyMTAChangeList().subscribe({
            next: (response: MTAChangeTypeList[]) => {
                if(response){
                    this.mtaChangeList.set(response);
                    this.fetchRequestChangeDetails();
                    if(this.policyService.isMTA()){
                        this.fetchBindIssuePolicyDetailsByPolicy();
                    }
                }
            },
            error: (error: any) => {
                console.log("error", error);
                this.toastService.addNotification({message: "System error. Failed to retrieve data. Please exit and contact help@AbacusAU.com.", type: ToastType.Error});
            }
        })
    }

    fetchRequestChangeDetails() {
        this.policyService.getRequestChangeDetails(Number(this.authService.getUserDetailsFromStorage()?.policyId))
        .subscribe({
            next: (response: any) => {
                if(response){
                    const {requestChangeType, requestDate, requestDescription, responseDescription, requestUserResponse,
                         reviewUserResponse, adjustmentLocked, responseChangeBound} = response;
                    this.policyAdministrationForm.patchValue({
                        requestChangeType: requestChangeType !== null ? requestChangeType : '',
                        requestDate: this.formatRequestDate(requestDate),
                        requestDescription: requestDescription,
                        responseDescription: responseDescription,
                        requestUserResponse: requestUserResponse,
                        responseChangeBound: responseChangeBound,
                        reviewUserResponse: reviewUserResponse,
                        adjustmentLocked: adjustmentLocked
                    });
                    const isDecline = (adjustmentLocked === 0 && reviewUserResponse === 0) || (adjustmentLocked === 1 && requestUserResponse === 0)
                    if((this.authService.isBroker() && !isDecline) || (this.authService.isUwAdmin())){
                        this.isReadOnly.set(true);
                    }
                }
            },
            error: (error: any) => {
                console.log("error", error);
            }
        })
    }

    fetchPolicyAdminsitrationDetails() {
        forkJoin([
            this.policyService.getPolicyMTAChangeList(),
            this.policyService.getRequestChangeDetails(Number(this.authService.getUserDetailsFromStorage()?.policyId))
        ]).pipe(
            map(([mtaChangeListDetails, mtaRequestChangeDetails]: [MTAChangeTypeList[], any]) => ({ mtaChangeListDetails, mtaRequestChangeDetails }))
        ).subscribe({   
            next:(response: any) => {
                this.mtaChangeList.set(response?.mtaChangeListDetails);
                const {requestChangeType, requestDate, requestDescription, responseDescription, requestUserResponse, 
                    reviewUserResponse, adjustmentLocked, responseChangeBound} = response.mtaRequestChangeDetails;
                this.policyAdministrationForm.patchValue({
                    requestChangeType: requestChangeType !== null ? requestChangeType : '',
                    requestDate: this.formatRequestDate(requestDate),
                    requestDescription: requestDescription,
                    responseDescription: responseDescription,
                    requestUserResponse: requestUserResponse,
                    responseChangeBound: responseChangeBound,
                    reviewUserResponse: reviewUserResponse,
                    adjustmentLocked: adjustmentLocked
                });

                if((this.authService.isBroker() && adjustmentLocked === 1) || (this.authService.isUwAdmin())){
                    this.isReadOnly.set(true);
                }
            },
            error: (error: any) => {
                console.log("error", error);
                this.toastService.addNotification({message: "System error. Failed to retrieve data. Please exit and contact help@AbacusAU.com.", type: ToastType.Error});
            }
        })
    }

    onConfirmationPopup(message: string){
        const dialog = this.dialog.open(ConfirmationDialogComponent, {
            data: { message: message }
        });
    
        return dialog.afterClosed()
    }

    handeRequestChangeMTA(payload: any) {
        this.policyService.requestMTAChangeByBroker(payload).subscribe({
            next: (response: any) => {
                if(response){
                    this.toastService.addNotification({message: "Your changes have been referred to the Underwriting Admin", type: ToastType.Success}); // success notification
                    this.indicationQuoteService.redirectToDashboard(); // redirect to dashboard
                }
            },
            error: (error: any) => {
                console.log("error", error);
                this.toastService.addNotification({message: "System error. Failed to save data. Please exit and contact help@AbacusAU.com.", type: ToastType.Error}); // error notification
            }
        });
    }

    handleDeclineChangeRequestMTA(payload: any) {
        this.policyService.declineMTA(Number(this.authService.getUserDetailsFromStorage()?.policyId), payload).subscribe({
            next: (response: any) => {
                if(response){
                    this.toastService.addNotification({message: "Change request declined successfully", type: ToastType.Success}); // success notification
                    this.indicationQuoteService.redirectToDashboard(); // redirect to dashboard
                }
            },
            error: (error: any) => {
                console.log("error", error);
                this.toastService.addNotification({message: "System error. Failed to save data. Please exit and contact help@AbacusAU.com.", type: ToastType.Error}); // error notification
            }
        });
    }

    handleQuoteMTA(payload: any) {
        this.policyService.quoteMTA(Number(this.authService.getUserDetailsFromStorage()?.policyId), Number(this.authService.getUserDetailsFromStorage()?.userDetails?.userId), payload)
        .subscribe({
            next: (response: any) => {
                if(response){
                    this.toastService.addNotification({message: "Policy amend successfully", type: ToastType.Success}); // success notification
                    this.isQuoteMTA.set(true); // show the submit Btn for QuoteMTA submit acion
                    this.quoteMTAEmitter.emit(true); // Move to Step 1 to update the details
                }
            },
            error: (error: any) => {
                console.log("error", error);
                this.toastService.addNotification({message: "System error. Failed to save data. Please exit and contact help@AbacusAU.com.", type: ToastType.Error}); // error notification
            }
        });
    }

    onAcceptMTAChange() {
        this.policyService.acceptMTAByBroker(Number(this.authService.getUserDetailsFromStorage()?.policyId),  Number(this.authService.getUserDetailsFromStorage()?.userDetails?.userId))
        .subscribe({
            next: (response: any) => {
                if(response){
                    this.toastService.addNotification({message: "Data saved successfully", type: ToastType.Success}); // success notification
                    this.indicationQuoteService.redirectToDashboard(); // redirect to dashboard
                }
            },
            error: (error: any) => {
                console.log("error", error);
                this.toastService.addNotification({message: "System error. Failed to save data. Please exit and contact help@AbacusAU.com.", type: ToastType.Error}); // error notification
            }
        })
    }

    onDeclineMTAChange() {
        this.policyService.declineMTAByBroker(Number(this.authService.getUserDetailsFromStorage()?.policyId))
        .subscribe({
            next: (response: any) => {
                if(response){
                    this.toastService.addNotification({message: "Data saved successfully", type: ToastType.Success}); // success notification
                    this.indicationQuoteService.redirectToDashboard(); // redirect to dashboard
                }
            },
            error: (error: any) => {
                console.log("error", error);
                this.toastService.addNotification({message: "System error. Failed to save data. Please exit and contact help@AbacusAU.com.", type: ToastType.Error}); // error notification
            }
        })
    }

    handleBindMTAChange() {
        this.policyService.bindApprovedMTAPolicy(Number(this.authService.getUserDetailsFromStorage()?.policyId), Number(this.authService.getUserDetailsFromStorage()?.userDetails?.userId))
        .subscribe({
            next: (response: any) => {
                if(response){
                    this.toastService.addNotification({message: "Data saved successfully", type: ToastType.Success}); // success notification
                    this.fetchPolicyAdminsitrationDetails();
                    //this.indicationQuoteService.redirectToDashboard(); // redirect to dashboard
                }
            },
            error: (error: any) => {
                console.log("error", error);
                this.toastService.addNotification({message: "System error. Failed to save data. Please exit and contact help@AbacusAU.com.", type: ToastType.Error}); // error notification
            }
        });
    }

    handleQuoteMTASubmit(payload: any) {
        this.policyService.saveQuoteMTAChange(Number(this.authService.getUserDetailsFromStorage()?.policyId), Number(this.authService.getUserDetailsFromStorage()?.userDetails?.userId), payload)
        .subscribe({
            next: (response: any) => {
                if(response){
                    this.toastService.addNotification({message: "Data saved successfully", type: ToastType.Success}); // success notification
                    this.indicationQuoteService.redirectToDashboard(); // redirect to dashboard
                }
            },
            error: (error: any) => {
                console.log("error", error);
                this.toastService.addNotification({message: "System error. Failed to save data. Please exit and contact help@AbacusAU.com.", type: ToastType.Error}); // error notification
            }
        });
    }

    onSaveAndExitHomeAction() {
        const dialog = this.dialog.open(ConfirmationDialogComponent, {
            data: { message: "Do you want to return to dashboard?" }
        });
      
        dialog.afterClosed().subscribe((result: boolean) => {
            if(result){
                this.indicationQuoteService.redirectToDashboard(); // redirect to dashboard
            }
        }) 
    }

    onRequestChange() {
        const msg = "Do you want to make changes with the Policy?"
        this.onConfirmationPopup(msg)
        .subscribe((result: boolean) => {
            if(result){  
                const {requestChangeType, requestDate, requestDescription} = this.policyAdministrationForm.getRawValue();
                const payload = {
                    requestChangeType: requestChangeType,
                    policyId: Number(this.authService.getUserDetailsFromStorage()?.policyId),
                    requestUserId: Number(this.authService.getUserDetailsFromStorage()?.userDetails?.userId),
                    requestDate: requestDate,
                    requestDescription: requestDescription,                  
                }   
                this.handeRequestChangeMTA(payload);
            }
        });
    }

    onDeclineRequestChange() {
        const msg = "Do you want to make changes with the Policy?"
        this.onConfirmationPopup(msg)
        .subscribe((result: boolean) => {
            if(result){ 
             const { responseDescription } = this.policyAdministrationForm.getRawValue();     
             const payload = {
                responseDescription: responseDescription
             }     
             this.handleDeclineChangeRequestMTA(payload);
            }
        });  
    }

    onQuoteMTAChange() {
        const msg = "Do you want to make changes with the Policy?"
        this.onConfirmationPopup(msg)
        .subscribe((result: boolean) => {
            if(result){        
                const { responseDescription } = this.policyAdministrationForm.getRawValue();  
                const payload = {
                    responseDescription: responseDescription
                }    
                this.handleQuoteMTA(payload);
            }
        });  
    }

    onBindMTAChange() {
        const msg = "Do you want to make changes with the Policy?"
        this.onConfirmationPopup(msg)
        .subscribe((result: boolean) => {
            if(result){              
             this.handleBindMTAChange();
            }
        });  
    }

    onQuoteMTASubmit(){
        const msg = "Do you want to make changes with the Policy?"
        this.onConfirmationPopup(msg)
        .subscribe((result: boolean) => {
            if(result){    
            const { responseDescription } = this.policyAdministrationForm.getRawValue();     
            const payload = {
                responseDescription: responseDescription
            }            
             this.handleQuoteMTASubmit(payload);
            }
        }); 
    }

    onViewandDownloadDocumentAction() {
        this.policyDocumentService.setDownloadEndorsementDocument(true); // trigger the policy document process
    }
}